import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { OnboardingService } from '../../../core/services/onboarding.service.ts.service';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import moment from 'moment';
import { IconComponent } from '../../../shared/icon/icon.component';
import provinceData from '../../../../assets/json/province.json';

@Component({
  selector: 'app-step-user-info',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IconComponent],
  templateUrl: './step-user-info.component.html',
  styleUrl: './step-user-info.component.scss',
})
export class StepUserInfoComponent implements OnInit {
  userInfoForm: FormGroup;

  encodedContact:
    | {
        employeeId: string;
        firstName: string;
        lastName: string;
      }
    | undefined = undefined;

  decodedContact:
    | {
        employeeId: string;
        firstName: string;
        lastName: string;
      }
    | undefined = undefined;

  // Anno corrente (da mettere come limite massimo per il campo anno di nascita)
  currentYear: number = new Date().getFullYear();

  isAdultUser: boolean | undefined = undefined;

  // json con tutte le province e comuni:
  provinces = provinceData;

  cities: string[] = [];

  constructor(
    private router: Router,
    private onboardingService: OnboardingService,
    private formBuilder: FormBuilder
  ) {
    // Validatore del form
    this.userInfoForm = this.formBuilder.group({
      firstName: ['', [Validators.required, this.noWhitespaceValidator]],
      lastName: ['', [Validators.required, this.noWhitespaceValidator]],
      gender: ['', [Validators.required]],
      birthDay: [
        '',
        [
          Validators.required,
          Validators.min(1),
          Validators.max(31),
          Validators.pattern('^\\d{1,2}$'),
        ],
      ],
      birthMonth: [
        '',
        [
          Validators.required,
          Validators.min(1),
          Validators.max(12),
          Validators.pattern('^\\d{1,2}$'),
        ],
      ],
      birthYear: [
        '',
        [
          Validators.required,
          Validators.min(1900),
          Validators.max(this.currentYear),
          Validators.pattern('^\\d{4}$'),
        ],
      ],
      birthCountry: ['', Validators.required],
      // I seguenti inizialmente sono required, ma cambiano a seconda del birthCountry selezionato
      birthProvince: [''],
      birthCity: [''],
      birthPlace: [''],
    });

    // Listener per monitorare i cambiamenti della data di nascita
    this.userInfoForm
      .get('birthDay')
      ?.valueChanges.subscribe(() => this.validateDate());
    this.userInfoForm
      .get('birthMonth')
      ?.valueChanges.subscribe(() => this.validateDate());
    this.userInfoForm
      .get('birthYear')
      ?.valueChanges.subscribe(() => this.validateDate());

    // Listener per monitorare i cambiamenti su birthCountry
    this.userInfoForm.get('birthCountry')?.valueChanges.subscribe((value) => {
      this.updateValidatorsForCountry(value);
      // Reset dei campi quando il paese cambia
      if (value !== 'Italia') {
        this.userInfoForm.get('birthProvince')?.reset('');
        this.userInfoForm.get('birthCity')?.reset();
      }
    });
  }

  ngOnInit(): void {
    // Recupera i dati salvati dal servizio
    const onboardingData = this.onboardingService.getData();
    this.encodedContact = onboardingData.employeeIdContact;

    // decodifica dati Employee ID e aggiornamento validatori nome e cognome
    if (this.encodedContact != undefined) {
      this.decodedContact = this.decodeEncryptedObject(this.encodedContact);
      // console.log('contatto', this.decodedContact);

      // update validazione nome
      this.userInfoForm
        .get('firstName')
        ?.setValidators([
          Validators.required,
          this.noWhitespaceValidator,
          this.nameValidator(this.decodedContact?.firstName),
        ]);

      // update validazione cognome
      this.userInfoForm
        .get('lastName')
        ?.setValidators([
          Validators.required,
          this.noWhitespaceValidator,
          this.nameValidator(this.decodedContact?.lastName),
        ]);
    }

    if (onboardingData) {
      // Pre-compila i campi del form se i dati sono disponibili
      this.userInfoForm.patchValue({
        firstName: onboardingData.firstName || '',
        lastName: onboardingData.lastName || '',
        gender: onboardingData.gender || '',
        birthDay: onboardingData.birthDay || '',
        birthMonth: onboardingData.birthMonth || '',
        birthYear: onboardingData.birthYear || '',
        birthCountry: onboardingData.birthCountry || '',
        birthProvince: onboardingData.birthProvince || '',
        birthCity: onboardingData.birthCity || '',
        birthPlace: onboardingData.birthPlace || '',
      });
    }
  }

  // Funzione per decriptare l'oggetto con i dati del contatto
  decodeEncryptedObject(encodedContact: {
    employeeId: string;
    firstName: string;
    lastName: string;
  }) {
    const decodeString = (encoded: string): string => {
      try {
        // Decodifica Base64
        const decoded = atob(encoded);

        // Decodifica eventuali caratteri speciali prima di invertire
        const decodedWithSpecialChars = decodeURIComponent(escape(decoded));

        // Ripristina l'ordine invertito della stringa
        return decodedWithSpecialChars.split('').reverse().join('');
      } catch (error) {
        console.error('Errore nella decodifica:', error);
        return '';
      }
    };

    return {
      employeeId: decodeString(encodedContact.employeeId),
      firstName: decodeString(encodedContact.firstName),
      lastName: decodeString(encodedContact.lastName),
    };
  }

  // Validator personalizzato per controllare se il nome inserito matcha con quello dell'Employee ID (non corrispondenza perfetta ma inclusione dei caratteri digitati, lunghezza minima 2 caratteri)
  nameValidator(decryptedName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const enteredName = control.value?.trim().toLowerCase();

      // Funzione per normalizzare la stringa (rimuovere spazi, apostrofi, accenti) e convertire in minuscolo
      const normalizeString = (str: string): string => {
        return str
          .normalize('NFD') // Normalizzazione Unicode per separare gli accenti
          .replace(/[\u0300-\u036f]/g, '') // Rimuove gli accenti
          .replace(/[^\w\s]/g, '') // Rimuove apostrofi, simboli, etc.
          .replace(/\s+/g, '') // Rimuove gli spazi
          .toLowerCase(); // Converti in minuscolo
      };

      const normalizedDecryptedName = normalizeString(decryptedName);
      const normalizedEnteredName = normalizeString(enteredName);
      // console.log('nome decriptato', normalizedDecryptedName);
      // console.log('nome immesso', normalizedEnteredName);

      // Verifica che il nome immesso sia lungo almeno 2 caratteri
      if (normalizedEnteredName.length < 2) {
        return { minLength: true }; // Errore di lunghezza minima
      }

      // Verifica se il nome immesso è contenuto nel nome decriptato
      return normalizedDecryptedName.includes(normalizedEnteredName)
        ? null // Nome valido
        : { nameMismatch: true }; // Nome non corrispondente
    };
  }

  // Funzione per aggiornare i validatori dinamicamente in base al campo birthCountry
  private updateValidatorsForCountry(country: string): void {
    const birthProvince = this.userInfoForm.get('birthProvince');
    const birthCity = this.userInfoForm.get('birthCity');
    const birthPlace = this.userInfoForm.get('birthPlace');

    if (country === 'Italia') {
      birthProvince?.setValidators([Validators.required]); // aggiungo required al validatore
      birthCity?.setValidators([Validators.required]); // aggiungo required al validatore
      birthPlace?.setValidators([]); // rimuovo l'eventuale required
      this.userInfoForm.get('birthPlace')?.reset();
      this.onboardingService.getData().birthPlace = undefined;
    } else if (country === 'Estero') {
      birthPlace?.setValidators([
        Validators.required,
        this.noWhitespaceValidator,
      ]); // aggiungo validatori required e no spazi vuoti al luogo di nascita
      birthProvince?.setValidators([]); // rimuovo l'eventuale required
      birthCity?.setValidators([]); // rimuovo l'eventuale required
      this.userInfoForm.get('birthProvince')?.reset();
      this.userInfoForm.get('birthCity')?.reset();
      this.onboardingService.getData().birthProvince = undefined;
      this.onboardingService.getData().birthCity = undefined;
    } else {
      // Rimuovo tutti i validatori se non è "Italia" né "Estero"
      birthProvince?.setValidators([]);
      birthCity?.setValidators([]);
      birthPlace?.setValidators([]);
    }

    // Rilevo i cambiamenti sui validatori
    birthProvince?.updateValueAndValidity();
    birthCity?.updateValueAndValidity();
    birthPlace?.updateValueAndValidity();
  }

  // Gestione della logica di cambiamento della provincia
  onProvinceChange(event: any): void {
    const selectedProvince = event.target.value;

    // Reset del campo birthCity quando cambia la provincia
    this.userInfoForm.get('birthCity')?.reset('');
  }

  get birthDay() {
    return this.userInfoForm.get('birthDay');
  }

  get birthMonth() {
    return this.userInfoForm.get('birthMonth');
  }

  get birthYear() {
    return this.userInfoForm.get('birthYear');
  }

  // Validazione formale della data
  private isValidDate(day: number, month: number, year: number): boolean {
    // Creazione della data usando moment
    const date = moment(`${day}-${month}-${year}`, 'DD-MM-YYYY');

    // Verifico se la data è valida
    return date.isValid();
  }

  // Validazione utente maggiorenne
  isAdultUserCheck(day: number, month: number, year: number): boolean {
    // Calcolo la data inserita
    const birthDate = moment(`${day}-${month}-${year}`, 'DD-MM-YYYY').startOf(
      'day'
    );

    // Calcolo data limite per utente maggiorenne
    const adultAgeLimit = moment().subtract(18, 'years').startOf('day');

    // Verifico se la data di nascita è almeno 18 anni fa
    return birthDate.isBefore(adultAgeLimit) || birthDate.isSame(adultAgeLimit);
  }

  // Validazione data completa
  validateDate() {
    const day = this.userInfoForm.get('birthDay')?.value;
    const month = this.userInfoForm.get('birthMonth')?.value;
    const year = this.userInfoForm.get('birthYear')?.value;

    if (day && month && year && !this.isValidDate(day, month, year)) {
      // Se la data non è valida, imposta un errore sui campi
      this.userInfoForm.get('birthDay')?.setErrors({ invalidDate: true });
      this.userInfoForm.get('birthMonth')?.setErrors({ invalidDate: true });
      this.userInfoForm.get('birthYear')?.setErrors({ invalidDate: true });
      this.isAdultUser = undefined; // Reset della validazione dell'età
      return; //
    } else {
      // Altrimenti, rimuovi eventuali errori
      this.userInfoForm.get('birthDay')?.setErrors(null);
      this.userInfoForm.get('birthMonth')?.setErrors(null);
      this.userInfoForm.get('birthYear')?.setErrors(null);
    }

    if (day && month && year && this.isValidDate(day, month, year)) {
      if (this.isAdultUserCheck(day, month, year)) {
        this.isAdultUser = true;
      } else {
        this.isAdultUser = false;
      }
    }

    // Forzo l'aggiornamento dello stato dei validatori
    this.birthDay?.updateValueAndValidity({
      onlySelf: false,
      emitEvent: false,
    });
    this.birthMonth?.updateValueAndValidity({
      onlySelf: false,
      emitEvent: false,
    });
    this.birthYear?.updateValueAndValidity({
      onlySelf: false,
      emitEvent: false,
    });
  }

  // Funzione di validazione per evitare spazi vuoti sui campi input
  private noWhitespaceValidator(
    control: any
  ): { [key: string]: boolean } | null {
    if (control.value && control.value.trim().length === 0) {
      return { whitespace: true }; // Restituisce errore se il campo contiene solo spazi
    }
    return null; // Restituisce null se il campo è valido
  }

  onSubmit() {
    if (this.userInfoForm.valid) {
      this.nextStep();
    }
  }

  nextStep() {
    // console.log('Form Submitted:', this.userInfoForm.value);

    // Salvo i valori nel servizio
    let userInfoFormData: {
      firstName: string;
      lastName: string;
      gender: string;
      birthDay: string;
      birthMonth: string;
      birthYear: string;
      birthDate: string;
      birthCountry: string;
      birthProvince?: string; // Aggiungi birthProvince come opzionale
      birthCity?: string; // Aggiungi birthCity come opzionale
      birthPlace?: string; // Aggiungi birthPlace come opzionale
    } = {
      firstName: this.userInfoForm.get('firstName')?.value.trim(),
      lastName: this.userInfoForm.get('lastName')?.value.trim(),
      gender: this.userInfoForm.get('gender')?.value,
      birthDay: String(this.userInfoForm.get('birthDay')?.value).padStart(
        2,
        '0'
      ),
      birthMonth: String(this.userInfoForm.get('birthMonth')?.value).padStart(
        2,
        '0'
      ),
      birthYear: String(this.userInfoForm.get('birthYear')?.value),
      birthDate: `${this.userInfoForm.get('birthYear')?.value}-${String(
        this.userInfoForm.get('birthMonth')?.value
      ).padStart(2, '0')}-${String(
        this.userInfoForm.get('birthDay')?.value
      ).padStart(2, '0')}`,
      birthCountry: this.userInfoForm.get('birthCountry')?.value,
    };

    if (this.userInfoForm.get('birthCountry')?.value === 'Italia') {
      userInfoFormData = {
        ...userInfoFormData,
        birthProvince: this.userInfoForm.get('birthProvince')?.value,
        birthCity: this.userInfoForm.get('birthCity')?.value,
      };
    }

    if (this.userInfoForm.get('birthCountry')?.value === 'Estero') {
      userInfoFormData = {
        ...userInfoFormData,
        birthPlace: this.userInfoForm.get('birthPlace')?.value.trim(),
      };
    }

    this.onboardingService.updateData({
      stepUserInfoCompleted: true,
      ...userInfoFormData,
    });
    this.router.navigate(['description']);
  }

  goBack() {
    this.router.navigate(['/']);
  }
}
